/**
 * Authentication context for managing user session state.
 * Provides login, logout, and session checking functionality.
 */
import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

/**
 * Hook to access authentication context.
 * @returns {Object} Authentication context value
 * @throws {Error} If used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Authentication provider component.
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const response = await authAPI.checkSession();
      if (response.data.authenticated) {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Session check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (userId, password, recaptchaToken) => {
    const response = await authAPI.login(userId, password, recaptchaToken);
    if (response.data.success) {
      setUser(response.data.user);
      return {
        success: true,
        mustChangePassword: response.data.mustChangePassword || false,
        passwordExpired: response.data.passwordExpired || false,
        passwordNonCompliant: response.data.passwordNonCompliant || false,
        message: response.data.message
      };
    }
    return {
      success: false,
      message: response.data.message
    };
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
